(() => {
  const shell = document.querySelector("[data-side-cart-shell]");
  const drawer = document.querySelector("[data-side-cart]");
  const itemContainer = document.querySelector("[data-side-cart-items]");
  const statusElement = document.querySelector("[data-side-cart-status]");
  const totalElement = document.querySelector("[data-side-cart-total]");
  const openButtons = Array.from(document.querySelectorAll("[data-cart-open]"));
  const closeButtons = Array.from(document.querySelectorAll("[data-cart-close]"));
  const countElements = Array.from(document.querySelectorAll("[data-cart-count]"));

  if (!shell || !drawer || !itemContainer) return;

  let activeCart = null;
  let isBusy = false;
  let lastFocusedElement = null;

  const escapeHtml = (value = "") =>
    String(value).replace(/[&<>"']/g, (character) => {
      const entities = {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#039;",
      };
      return entities[character];
    });

  const formatMoney = (cents, currency = "CHF") => {
    const amount = (cents || 0) / 100;
    if (currency === "CHF") {
      return Number.isInteger(amount) ? `CHF ${amount.toFixed(0)}.-` : `CHF ${amount.toFixed(2)}`;
    }

    return new Intl.NumberFormat("de-CH", {
      style: "currency",
      currency,
    }).format(amount);
  };

  const setStatus = (message) => {
    if (statusElement) statusElement.textContent = message;
  };

  const setBusy = (busy) => {
    isBusy = busy;
    drawer.classList.toggle("is-loading", busy);
    drawer.setAttribute("aria-busy", busy ? "true" : "false");
  };

  const setOpen = (isOpen) => {
    if (isOpen) {
      lastFocusedElement = document.activeElement;
      shell.hidden = false;
      requestAnimationFrame(() => shell.classList.add("is-open"));
      document.documentElement.classList.add("side-cart-open");
      drawer.focus({ preventScroll: true });
      return;
    }

    shell.classList.remove("is-open");
    document.documentElement.classList.remove("side-cart-open");
    window.setTimeout(() => {
      if (!shell.classList.contains("is-open")) {
        shell.hidden = true;
        if (lastFocusedElement && typeof lastFocusedElement.focus === "function") {
          lastFocusedElement.focus({ preventScroll: true });
        }
      }
    }, 260);
  };

  const updateCount = (count) => {
    countElements.forEach((element) => {
      element.textContent = String(count || 0);
    });
  };

  const itemImage = (item) => {
    if (item.featured_image?.url) return item.featured_image.url;
    if (item.image) return item.image;
    return "";
  };

  const renderCart = (cart) => {
    activeCart = cart;
    const currency = cart.currency || "CHF";

    updateCount(cart.item_count);
    drawer.classList.toggle("is-empty", cart.item_count === 0);

    if (totalElement) {
      totalElement.textContent = formatMoney(cart.total_price, currency);
    }

    setStatus(
      cart.item_count === 1
        ? "Ein Produkt ist im Warenkorb."
        : `${cart.item_count || 0} Produkte sind im Warenkorb.`
    );

    itemContainer.innerHTML = cart.items
      .map((item, index) => {
        const image = escapeHtml(itemImage(item));
        const title = escapeHtml(item.product_title || item.title);
        const url = escapeHtml(item.url || "/cart");
        const variantTitle =
          item.variant_title && item.variant_title !== "Default Title"
            ? `<small class="side-cart-item-variant">${escapeHtml(item.variant_title)}</small>`
            : "";
        const sellingPlan =
          item.selling_plan_allocation?.selling_plan?.name
            ? `<small class="side-cart-item-variant">${escapeHtml(item.selling_plan_allocation.selling_plan.name)}</small>`
            : "";

        return `
          <article class="side-cart-item" data-cart-line="${index + 1}">
            ${
              image
                ? `<img class="side-cart-item-image" src="${image}" alt="${title}" loading="lazy">`
                : `<div class="side-cart-item-image" aria-hidden="true"></div>`
            }
            <div class="side-cart-item-main">
              <a class="side-cart-item-title" href="${url}">${title}</a>
              ${variantTitle}
              ${sellingPlan}
              <div class="side-cart-quantity" aria-label="Menge">
                <button type="button" data-cart-quantity="-1" aria-label="Menge reduzieren">&minus;</button>
                <span>${item.quantity}</span>
                <button type="button" data-cart-quantity="1" aria-label="Menge erhoehen">+</button>
              </div>
            </div>
            <div class="side-cart-item-side">
              <button class="side-cart-remove" type="button" data-cart-remove aria-label="${title} entfernen">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M4 7h16"></path>
                  <path d="M10 11v6"></path>
                  <path d="M14 11v6"></path>
                  <path d="M6 7l1 14h10l1-14"></path>
                  <path d="M9 7V4h6v3"></path>
                </svg>
              </button>
              <strong class="side-cart-item-price">${formatMoney(item.final_line_price, currency)}</strong>
            </div>
          </article>
        `;
      })
      .join("");
  };

  const fetchCart = async () => {
    const response = await fetch("/cart.js", {
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error("Cart could not be loaded");
    const cart = await response.json();
    renderCart(cart);
    return cart;
  };

  const changeLine = async (line, quantity) => {
    if (isBusy) return;
    setBusy(true);

    try {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ line, quantity }),
      });
      if (!response.ok) throw new Error("Cart line could not be changed");
      renderCart(await response.json());
    } finally {
      setBusy(false);
    }
  };

  openButtons.forEach((button) => {
    button.addEventListener("click", async (event) => {
      event.preventDefault();
      setOpen(true);
      if (!activeCart) {
        setBusy(true);
        try {
          await fetchCart();
        } finally {
          setBusy(false);
        }
      }
    });
  });

  closeButtons.forEach((button) => {
    button.addEventListener("click", () => setOpen(false));
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !shell.hidden) {
      setOpen(false);
    }
  });

  itemContainer.addEventListener("click", (event) => {
    const item = event.target.closest("[data-cart-line]");
    if (!item || !activeCart || isBusy) return;

    const line = Number(item.dataset.cartLine);
    const cartItem = activeCart.items[line - 1];
    if (!cartItem) return;

    if (event.target.closest("[data-cart-remove]")) {
      changeLine(line, 0);
      return;
    }

    const quantityButton = event.target.closest("[data-cart-quantity]");
    if (!quantityButton) return;

    const delta = Number(quantityButton.dataset.cartQuantity);
    changeLine(line, Math.max(0, cartItem.quantity + delta));
  });

  document.querySelectorAll(".product-shopify-form").forEach((form) => {
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (isBusy) return;

      const submitButton = form.querySelector('[type="submit"]');
      setBusy(true);
      submitButton?.setAttribute("aria-busy", "true");

      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: { Accept: "application/json" },
          body: new FormData(form),
        });

        if (!response.ok) {
          form.submit();
          return;
        }

        await fetchCart();
        setOpen(true);
      } finally {
        submitButton?.removeAttribute("aria-busy");
        setBusy(false);
      }
    });
  });

  fetchCart().catch(() => {});
})();
